import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { motion } from 'framer-motion'
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, Shield } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { resetAnalystPassword } from '../lib/api'
import { CLIENT_RETURN_TO_KEY, safeClientReturn } from '../lib/sessionKeys'

// DW-RESET-PASSWORD-LANDING.
//
// The set-password email links to {FRONTEND_URL}/reset-password?token=…
// (backend /auth/v2/analyst/forgot-password issues the token). Before this
// page existed the SPA had no /reset-password route, so the catch-all bounced
// the visitor to the login screen and dropped the token — the emailed link
// could never set a password.
//
// This is a PUBLIC landing (the visitor holds no session yet):
//   - The token is a capability secret: it goes only in the request body,
//     never into visible copy.
//   - A link without a well-formed token (exactly 64 hex chars) can never
//     succeed — say so calmly, render NO password fields, make zero calls.
//   - Success sets the password and NOTHING else: no session is minted, no
//     token stored, no redirect into the app. The visitor signs in at /login.
//   - A 400 is rendered as an honest, generic failure — it never confirms
//     whether any account exists.

const TOKEN_SHAPE = /^[0-9a-fA-F]{64}$/
const MIN_PASSWORD_LENGTH = 12

// BRIEF-B4c: the route's exact refusal when there is no mobile to text the
// sign-in code to (backend app/auth/sign_in_code.py NO_PHONE_MESSAGE). A fresh
// link would be refused the same way, so the page says what will work.
const NO_PHONE_MESSAGE = 'Ask your security team to send a new sign-in invitation.'

type Phase = 'form' | 'submitting' | 'done'

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const usable = token != null && TOKEN_SHAPE.test(token)

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [phase, setPhase] = useState<Phase>('form')

  // BRIEF-B11: a /client/... return-to (dw_return_to, the same one LoginPage
  // reads — an owner on the way to the staff notice) is where Sign in goes:
  // the guard there bounces to the ordinary sign-in and back. Anything else
  // goes to the ordinary sign-in. This owner has just set a password and has
  // no authenticator yet, so the page never sends them into the tripwire
  // form: no tripwire copy here, and a saved return-to that is not /client/...
  // (a leftover /client or tripwire link, maybe in localStorage when the
  // emailed link opened in a new tab) is dropped so /login is not drawn as
  // the tripwire form either.
  const [returnTo] = useState(
    () => sessionStorage.getItem(CLIENT_RETURN_TO_KEY) ?? localStorage.getItem(CLIENT_RETURN_TO_KEY),
  )
  const signInTarget = safeClientReturn(returnTo) ?? '/login'

  useEffect(() => {
    if (returnTo && !safeClientReturn(returnTo)) {
      sessionStorage.removeItem(CLIENT_RETURN_TO_KEY)
      localStorage.removeItem(CLIENT_RETURN_TO_KEY)
    }
  }, [returnTo])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token || phase === 'submitting') return

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Please choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords don’t match. Please re-enter them.')
      return
    }

    setError('')
    setPhase('submitting')
    try {
      await resetAnalystPassword(token, newPassword, confirmPassword)
      setPhase('done')
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.detail === NO_PHONE_MESSAGE) {
        setError(NO_PHONE_MESSAGE)
        setPhase('form')
        return
      }
      // Honest, generic failure: the token may be expired/used or the
      // password rejected. Never hints at whether an account exists.
      setError(
        'We couldn’t set this password. The link may have expired or already been used — you can request a fresh one from the sign-in page.',
      )
      setPhase('form')
    }
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8"
      >
        {!usable ? (
          <div className="text-center" role="status" aria-live="polite">
            <div className="w-14 h-14 rounded-2xl bg-danger-light flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-danger" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              This link is not usable
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              It looks incomplete or damaged. Please open the most recent link
              from your email, or request a fresh one from the sign-in page.
            </p>
            <Link
              to={signInTarget}
              className="inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
            >
              Go to sign in
            </Link>
          </div>
        ) : phase === 'done' ? (
          <div className="text-center" role="status" aria-live="polite">
            <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-brass" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              Password is set
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Sign in to your security team.
            </p>
            <Link
              to={signInTarget}
              className="inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
            >
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center mb-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-brass" aria-hidden="true" />
              </div>
              <h1 className="text-xl font-serif font-bold text-navy">
                Set your password
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                {`Choose a password to use at your first meeting. At least ${MIN_PASSWORD_LENGTH} characters.`}
              </p>
            </div>

            {error && (
              <div
                className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                role="alert"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-medium text-navy mb-1"
                >
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                  autoComplete="new-password"
                  className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-navy mb-1"
                >
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                  autoComplete="new-password"
                  className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={phase === 'submitting'}
                className="w-full tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {phase === 'submitting' && (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                )}
                Set password
              </button>
            </form>

            <p className="text-sm text-gray-500 mt-5 text-center">
              <Link
                to="/login"
                className="text-brass hover:text-brass-dark font-medium"
              >
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  )
}
